import { NAV_GROUPS } from "@/lib/navigation";
import { BLOG_POSTS } from "@/lib/blog";
import { GUIDES, LEAGUE_CLUSTERS, MARKET_CLUSTERS, slugify } from "@/lib/seoClusters";
import { PREDICTION_LANDINGS } from "@/lib/predictionLandings";
import { SPORT_PREVIEWS } from "@/lib/sportPreviewLandings";
import { COMPETITIONS, listCountries } from "@/lib/entities";
import { US_SPORTS, SPORT_CATEGORY_LABELS } from "@/lib/oddsSports";

/**
 * Site-wide search index.
 *
 * Every public surface (navigation, prediction hubs, league/market clusters,
 * sport hubs, guides, blog/news, competitions, countries) is flattened into one
 * searchable list so a single search bar can answer "where do I find X?".
 *
 * Admin surfaces are excluded by construction: `ADMIN_PREFIXES` filters any
 * entry whose URL is an admin route, so admin pages can never leak into
 * results even if a registry links them.
 *
 * Each entry also carries facets (sport / league / competition / content type)
 * so the UI can narrow results without a second index.
 */

export type SearchGroup =
  | "Predictions"
  | "Leagues & competitions"
  | "Markets"
  | "Sports"
  | "Guides"
  | "News & blog"
  | "Countries"
  | "Pages"
  | "Fixtures";

/** Coarse content type used by the "type" filter. Mirrors SearchGroup. */
export type SearchContentType = SearchGroup;

export interface SearchFacet {
  /** Stable filter value. */
  value: string;
  /** Human label shown in the filter UI. */
  label: string;
  /** How many indexed entries carry this facet. */
  count: number;
}

export interface SearchEntry {
  id: string;
  title: string;
  subtitle?: string;
  url: string;
  group: SearchGroup;
  /** Extra terms that should match this entry (never displayed). */
  keywords?: string[];
  /** Sport facet, e.g. "football", "basketball", "american-football". */
  sport?: string;
  /** Human label for the sport facet. */
  sportLabel?: string;
  /** League facet — a LEAGUE_CLUSTERS slug or odds-catalog league slug. */
  league?: string;
  leagueLabel?: string;
  /** Competition facet — a COMPETITIONS slug. */
  competition?: string;
  competitionLabel?: string;
}

const ADMIN_PREFIXES = ["/admin", "/match-curation", "/upload", "/i18n-audit"];

export function isAdminUrl(url: string): boolean {
  const path = url.split("?")[0]!.toLowerCase();
  return ADMIN_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/** Lowercase + strip diacritics so "atlético" matches "atletico" and vice-versa. */
export function normalizeSearchText(value: string): string {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s/+.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const FOOTBALL = { sport: "football", sportLabel: "Football / Soccer" } as const;

/**
 * Map an odds-catalog sport onto its public routes so navigation entries and
 * hub links inherit the right sport facet instead of defaulting to football.
 */
function sportFacetForUrl(url: string): { sport: string; sportLabel: string } | null {
  const path = url.split("?")[0]!.toLowerCase();
  for (const s of US_SPORTS) {
    if (path === `/${s.slug}-predictions` || path.startsWith(`/${s.slug}-predictions/`)) {
      return { sport: s.category, sportLabel: SPORT_CATEGORY_LABELS[s.category] ?? s.label };
    }
  }
  return null;
}

function buildStaticIndex(): SearchEntry[] {
  const entries: SearchEntry[] = [];
  const seen = new Set<string>();

  const push = (entry: SearchEntry) => {
    if (isAdminUrl(entry.url)) return;
    const key = `${entry.group}::${entry.url}`;
    if (seen.has(key)) return;
    seen.add(key);
    entries.push(entry);
  };

  for (const group of NAV_GROUPS) {
    if (group.url) {
      push({
        id: `nav:${group.url}`,
        title: group.label,
        url: group.url,
        group: "Pages",
        keywords: [group.label],
        ...(sportFacetForUrl(group.url) ?? {}),
      });
    }
    for (const leaf of group.items) {
      push({
        id: `nav:${leaf.url}`,
        title: leaf.title,
        subtitle: leaf.description ?? group.label,
        url: leaf.url,
        group: "Pages",
        keywords: [group.label, leaf.title],
        ...(sportFacetForUrl(leaf.url) ?? {}),
      });
    }
  }

  // Odds-catalog sports get first-class entries (not only nav links) so every
  // launched league is searchable and filterable by sport.
  for (const s of US_SPORTS) {
    push({
      id: `odds-sport:${s.slug}`,
      title: `${s.label} Predictions`,
      subtitle: SPORT_CATEGORY_LABELS[s.category] ?? s.label,
      url: `/${s.slug}-predictions`,
      group: "Sports",
      keywords: [s.label, s.slug, s.league, s.oddsApiKey, SPORT_CATEGORY_LABELS[s.category] ?? ""],
      sport: s.category,
      sportLabel: SPORT_CATEGORY_LABELS[s.category] ?? s.label,
      league: s.slug,
      leagueLabel: s.label,
    });
  }

  for (const landing of PREDICTION_LANDINGS) {
    const leagueCluster = LEAGUE_CLUSTERS.find((c) => c.slug === landing.slug);
    push({
      id: `landing:${landing.slug}`,
      title: landing.label,
      subtitle: landing.description,
      url: `/predictions/${landing.slug}`,
      group: "Predictions",
      keywords: [landing.h1, landing.keywords, ...(landing.leagueMatchers ?? []), ...(landing.marketMatchers ?? [])],
      ...FOOTBALL,
      league: leagueCluster?.slug!,
      leagueLabel: leagueCluster?.label!,
    });
  }

  for (const sport of SPORT_PREVIEWS) {
    const s = sport as unknown as Record<string, string>;
    const sportSlug = slugify(s["sport"] ?? s["label"] ?? s["slug"]!);
    push({
      id: `sport:${s["slug"]}`,
      title: s["label"] ?? s["h1"] ?? s["slug"]!,
      subtitle: s["description"]!,
      url: `/predictions/${s["slug"]}`,
      group: "Sports",
      keywords: [s["title"], s["keywords"]].filter(Boolean) as string[],
      sport: sportSlug,
      sportLabel: s["sport"] ?? s["label"] ?? s["slug"]!,
    });
  }

  for (const league of LEAGUE_CLUSTERS) {
    push({
      id: `league:${league.slug}`,
      title: league.label,
      subtitle: league.description,
      url: `/leagues/${league.slug}`,
      group: "Leagues & competitions",
      keywords: league.matchers,
      ...FOOTBALL,
      league: league.slug,
      leagueLabel: league.label,
    });
  }

  for (const market of MARKET_CLUSTERS) {
    push({
      id: `market:${market.slug}`,
      title: market.label,
      subtitle: market.description,
      url: `/markets/${market.slug}`,
      group: "Markets",
      keywords: market.matchers,
      ...FOOTBALL,
    });
  }

  for (const guide of GUIDES) {
    push({
      id: `guide:${guide.slug}`,
      title: guide.label,
      subtitle: guide.description,
      url: `/guides/${guide.slug}`,
      group: "Guides",
      keywords: [
        ...guide.matchers,
        ...(guide.faqs ?? []).map((f) => f.question),
        ...(guide.body ?? []).map((s) => s.heading),
      ],
    });
  }

  for (const post of BLOG_POSTS) {
    push({
      id: `blog:${post.slug}`,
      title: post.title,
      subtitle: `${post.category === "news" ? "News" : "Analysis"} · ${post.date}`,
      url: `/blog/${post.slug}`,
      group: "News & blog",
      keywords: [...post.tags!, post.description ?? "", ...post.sections.map((s) => s.heading)].filter((k): k is string => typeof k === "string"),
    });
  }

  for (const comp of COMPETITIONS) {
    push({
      id: `competition:${comp.slug}`,
      title: comp.label,
      subtitle: `${comp.countryLabel} · ${comp.competitionType}`,
      url: `/competitions/${comp.slug}`,
      group: "Leagues & competitions",
      keywords: comp.matchers,
      ...FOOTBALL,
      competition: comp.slug,
      competitionLabel: comp.label,
    });
  }

  for (const country of listCountries()) {
    push({
      id: `country:${country.slug}`,
      title: country.label,
      subtitle: "Country hub",
      url: `/countries/${country.slug}`,
      group: "Countries",
    });
  }

  return entries;
}

let cached: SearchEntry[] | null = null;

export function siteSearchIndex(): SearchEntry[] {
  if (!cached) cached = buildStaticIndex();
  return cached;
}

export interface SearchFilters {
  sport?: string | undefined;
  league?: string | undefined;
  competition?: string | undefined;
  contentType?: SearchContentType | undefined;
  /** Display label for the active league filter (used for live DB queries). */
  leagueLabel?: string | undefined;

}

/**
 * Relevance weights. Every value is additive on top of the deterministic
 * text score, so tuning a boost changes ranking without changing what matches.
 */
export interface SearchBoosts {
  /** Boost when the entry belongs to the filtered/active sport. */
  sport?: number;
  /** Boost when the entry belongs to the filtered/active league. */
  league?: number;
  /** Boost when the entry belongs to the filtered/active competition. */
  competition?: number;
  /** Per-content-type boosts, e.g. `{ Predictions: 15, Guides: -5 }`. */
  contentType?: Partial<Record<SearchContentType, number>>;
}

export const DEFAULT_SEARCH_BOOSTS: SearchBoosts = {
  sport: 10,
  league: 12,
  competition: 12,
  contentType: {},
};

export interface SearchOptions extends SearchFilters {
  limit?: number;
  /** Drop results scoring below this threshold (relevance control). */
  minScore?: number;
  /** Require every term to match (no broad single-term fallback). */
  strict?: boolean;
  /**
   * Preferred sport/league/competition used for boosting only (not filtering) —
   * lets the current page context lift relevant results without hiding others.
   */
  preferSport?: string;
  preferLeague?: string;
  preferCompetition?: string;
  /** Override the default relevance weights. */
  boosts?: SearchBoosts;
}


export function hasActiveFilters(f: SearchFilters): boolean {
  return !!(f.sport || f.league || f.competition || f.contentType);
}

function matchesFilters(entry: SearchEntry, f: SearchFilters): boolean {
  if (f.contentType && entry.group !== f.contentType) return false;
  if (f.sport && entry.sport !== f.sport) return false;
  if (f.league && entry.league !== f.league) return false;
  if (f.competition && entry.competition !== f.competition) return false;
  return true;
}

/** Facet options for the filter UI, derived from the index so they never drift. */
export function searchFacets(filters: SearchFilters = {}): {
  sports: SearchFacet[];
  leagues: SearchFacet[];
  competitions: SearchFacet[];
  contentTypes: SearchFacet[];
} {
  const sports = new Map<string, SearchFacet>();
  const leagues = new Map<string, SearchFacet>();
  const competitions = new Map<string, SearchFacet>();
  const contentTypes = new Map<string, SearchFacet>();

  const bump = (map: Map<string, SearchFacet>, value?: string, label?: string) => {
    if (!value) return;
    const existing = map.get(value);
    if (existing) existing.count += 1;
    else map.set(value, { value, label: label ?? value, count: 1 });
  };

  for (const entry of siteSearchIndex()) {
    bump(sports, entry.sport, entry.sportLabel);
    bump(contentTypes, entry.group, entry.group);
    // League/competition lists respect an active sport filter so the dropdowns
    // stay relevant to what the user already narrowed to.
    if (!filters.sport || entry.sport === filters.sport) {
      bump(leagues, entry.league, entry.leagueLabel);
      bump(competitions, entry.competition, entry.competitionLabel);
    }
  }

  const sort = (m: Map<string, SearchFacet>) =>
    [...m.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return {
    sports: sort(sports),
    leagues: sort(leagues),
    competitions: sort(competitions),
    contentTypes: sort(contentTypes),
  };
}

interface Scored {
  entry: SearchEntry;
  score: number;
}

/**
 * Case- and accent-insensitive multi-term ranking with optional facet filters.
 * Every whitespace-separated term must match somewhere in the entry;
 * earlier/exact/title matches rank higher than keyword-only matches.
 *
 * Filters are applied BEFORE scoring, and admin routes are excluded at index
 * build time, so no filter combination can surface an admin surface.
 */
export function searchSiteIndex(
  query: string,
  optionsOrLimit: SearchOptions | number = {},
): SearchEntry[] {
  const options: SearchOptions =
    typeof optionsOrLimit === "number" ? { limit: optionsOrLimit } : optionsOrLimit;
  const { limit = 40, minScore = 0, strict = false } = options;

  const q = normalizeSearchText(query);
  const filtersActive = hasActiveFilters(options);

  // With filters but no query, list the filtered surfaces (browse mode).
  if (!q) {
    if (!filtersActive) return [];
    return siteSearchIndex()
      .filter((e) => matchesFilters(e, options))
      .sort((a, b) => a.title.localeCompare(b.title))
      .slice(0, limit);
  }

  const terms = q.split(" ").filter(Boolean);

  const scored: Scored[] = [];
  const partial: Scored[] = [];
  for (const entry of siteSearchIndex()) {
    if (!matchesFilters(entry, options)) continue;

    const title = normalizeSearchText(entry.title);
    const subtitle = normalizeSearchText(entry.subtitle ?? "");
    const keywords = normalizeSearchText((entry.keywords ?? []).join(" "));
    const url = normalizeSearchText(entry.url);
    const haystack = `${title} ${subtitle} ${keywords} ${url}`;

    let score = 0;
    let matchedAll = true;
    for (const term of terms) {
      if (!haystack.includes(term)) {
        matchedAll = false;
        break;
      }
      if (title === term) score += 100;
      else if (title.startsWith(term)) score += 50;
      else if (title.includes(term)) score += 30;
      else if (subtitle.includes(term)) score += 12;
      else if (keywords.includes(term)) score += 8;
      else score += 3;
    }
    // Prefer shorter, more specific titles on equal relevance.
    score += Math.max(0, 20 - title.length / 4);
    // Relevance boosts: facet filters AND soft page-context preferences.
    const b = { ...DEFAULT_SEARCH_BOOSTS, ...(options.boosts ?? {}) };
    const wantSport = options.sport ?? options.preferSport;
    const wantLeague = options.league ?? options.preferLeague;
    const wantCompetition = options.competition ?? options.preferCompetition;
    if (wantSport && entry.sport === wantSport) score += b.sport ?? 0;
    if (wantLeague && entry.league === wantLeague) score += b.league ?? 0;
    if (wantCompetition && entry.competition === wantCompetition)
      score += b.competition ?? 0;
    score += b.contentType?.[entry.group] ?? 0;


    if (matchedAll) {
      scored.push({ entry, score });
    } else if (!strict && terms.some((term) => haystack.includes(term))) {
      // Broad fallback so a mixed query ("atletico over 2.5") still surfaces
      // the closest hubs instead of an empty list.
      partial.push({ entry, score: score / 4 });
    }
  }

  const ranked = scored.length > 0 ? scored : partial;
  ranked.sort((a, b) => b.score - a.score);
  return ranked
    .filter((s) => s.score >= minScore)
    .slice(0, limit)
    .map((s) => s.entry);
}

export function groupResults(results: SearchEntry[]): Array<[SearchGroup, SearchEntry[]]> {
  const map = new Map<SearchGroup, SearchEntry[]>();
  for (const r of results) {
    const list = map.get(r.group) ?? [];
    list.push(r);
    map.set(r.group, list);
  }
  return [...map.entries()];
}
